import { useEffect, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useI18n } from "../i18n";
import { CONSENT_REQUIRED_EVENT } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { consentUrl, hasCurrentConsent } from "../lib/consent";
import { isMockApi } from "../lib/estimateApi";
import { useMe } from "../lib/queries";
import { Skeleton } from "./Skeleton";

/**
 * ครอบหน้าที่จะส่งรูปให้ AI (S04): ผู้ใช้ที่เข้าสู่ระบบแล้วแต่ยังไม่ยินยอมเวอร์ชันปัจจุบัน ถูกพาไป /consent?returnTo=...
 * ตรวจล่วงหน้าจาก GET /me (ไม่ต้องรอ 403) ส่วนการบังคับจริงอยู่ที่ server
 * ผู้ใช้ที่ยังไม่เข้าสู่ระบบ หรือโหลด /me ไม่ได้ (ออฟไลน์) ผ่านไปก่อน หน้าปลายทางจัดการเอง และ server ตอบ 403 ได้
 */
export function ConsentGate({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const { status } = useAuth();
  const me = useMe();
  const loc = useLocation();
  const active = isMockApi() || status === "authenticated";
  if (!active || me.isError) return <>{children}</>;
  if (me.isPending) return <div className="page" aria-busy="true" aria-label={t("web.consent.checking")}><div className="container"><Skeleton shape="title" /></div></div>;
  if (!hasCurrentConsent(me.data)) return <Navigate to={consentUrl(loc.pathname + loc.search)} replace state={{ forced: true }} />;
  return <>{children}</>;
}

/** ฟัง 403 consent_required จากทุกจุดที่เรียก API (gate, upload, บันทึกงาน ฯลฯ) แล้วพาไปหน้ายินยอม กลับมาที่หน้าเดิมหลังยินยอม */
export function ConsentRedirector() {
  const nav = useNavigate();
  const loc = useLocation();
  const qc = useQueryClient();
  const here = loc.pathname + loc.search;
  useEffect(() => {
    const on = () => {
      void qc.invalidateQueries({ queryKey: ["me"] });
      if (loc.pathname === "/consent" || loc.pathname === "/privacy") return;
      nav(consentUrl(here), { state: { forced: true } });
    };
    window.addEventListener(CONSENT_REQUIRED_EVENT, on);
    return () => window.removeEventListener(CONSENT_REQUIRED_EVENT, on);
  }, [nav, qc, here, loc.pathname]);
  return null;
}
